// src/encounter/lib/flag-offer.ts
//
// Which flag types the rail "Add flag" picker may OFFER, and to whom.
//
// Encounter owner rulings, 1 Nov 2026, in `~/.claude/context/encounter-clinical-informatics-authority.md`:
//   · "chart rail + form fields": no type is offered until its persona list exists, and the list
//     excludes allergy-type, demographic and diagnosis-bearing flags;
//   · "flag types offered from the chart rail, row by row": the persona list below.
//
// THE KEY IS THE MASTER CODE: `FlagCategory.code` on each `GetFlagList` row, the same numeric
// `flagResponse.code` the CreateFlag write carries. Codes as exported by Andrew from the dev
// Scheduling `AppointmentFlags` master (1 Nov 2026; there is no code 17). The server-side copy of
// this list (PROD-3371) keys on the same codes, and the two must stay the same list. Display text
// is never matched: a renamed or misspelt master row keeps its code.
//
// ANY CODE NOT LISTED IS NOT OFFERED, including a row the master adds later. Ruled NEVER from the
// rail, so deliberately absent: 1 Active TB, 2 H/O Alcohol Abuse, 3 H/O Drug Addiction (they name
// diagnoses), 9 Allergic to Penicillin and 10 Allergic to Sulfa Drugs (they are allergies: "Record
// as allergy"), 20 Divorced (demographic). Ruled NOT OFFERED until the master defines it: 21 Abuse.
//
// The server is the authority on every write (PROD-3371); this is the UI's half.

import type { FlagCategoryGroup } from '@/types/flags-api';
import { isAlliedHealth, isNurse, mayOrder } from '@/auth/roles';
import type { UserData } from '@/auth/types';

/** The three clinical personas the ruling names: P, N and A. */
export type FlagPersona = 'physician' | 'nurse' | 'allied-health';

const P: FlagPersona = 'physician';
const N: FlagPersona = 'nurse';
const A: FlagPersona = 'allied-health';

/** Master flag code → the personas who may add it. One entry per ruled row. */
export const FLAG_PERSONA_ALLOWLIST: ReadonlyMap<number, readonly FlagPersona[]> = new Map<number, readonly FlagPersona[]>([
  // CHART
  [4, [P]], // Hospice Care
  [5, [P]], // Immunosuppression
  [6, [P, N]], // No Blood Products (the patient's stated refusal)
  [7, [P]], // Terminally Ill
  [8, [P]], // Work Restrictions
  [11, [P]], // No Narcotics
  [12, [P]], // No Refills Until Next Visit
  // COMMUNICATION
  [13, [P, N, A]], // Deaf
  [14, [P, N, A]], // Impaired Hearing
  [15, [P, N, A]], // Interpreter Required
  [16, [P, N, A]], // Mute
  [18, [P, N]], // Memory Problems
  // GENERAL
  [24, [P, N, A]], // Needs Ambulance
  [25, [P, N]], // Needs Attender All The Time
  [26, [P, N, A]], // Bed Ridden
  [27, [P, N, A]], // Walks With A Cane
  [28, [P, N, A]], // Uses Hearing Aid
  // CONFIDENTIAL
  [19, [P, N]], // No History Sharing With Family
  [22, [P, N]], // Suicidal
  [23, [P, N]], // Homicidal
]);

/** The row's numeric master code; the wire has been seen sending numbers as strings. */
function flagCode(raw: unknown): number | null {
  const n = typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : raw;
  return typeof n === 'number' && Number.isInteger(n) ? n : null;
}

/**
 * The signed-in user's flag persona, from the app's role source (`@/auth/roles`):
 * physician = the physician allowlist (`mayOrder`, Scrips.Common {3, 8, 18}); nurse = `isNurse`;
 * allied health = `isAlliedHealth`. Anyone else, or no user, is `null` and is offered nothing.
 * Deliberately NOT `clinicalRoleFor`, whose default reads an unrecognised user as a physician.
 */
export function flagPersonaFor(
  user: Pick<UserData, 'role' | 'tenantRoleId' | 'isOnboarded'> | null | undefined,
): FlagPersona | null {
  if (mayOrder(user)) return P;
  if (isNurse(user)) return N;
  if (isAlliedHealth(user)) return A;
  return null;
}

/**
 * Keep only the flag types this persona may add, and drop any group left with none (so a heading
 * never sits over nothing). No persona → nothing.
 */
export function offerableFlagGroups(
  groups: readonly FlagCategoryGroup[] | null | undefined,
  persona: FlagPersona | null,
  allowlist: ReadonlyMap<number, readonly FlagPersona[]> = FLAG_PERSONA_ALLOWLIST,
): FlagCategoryGroup[] {
  if (!persona) return [];
  return (groups ?? [])
    .map((g) => ({
      ...g,
      flagResponse: (g.flagResponse ?? []).filter((c) => {
        const code = flagCode(c.code);
        return code !== null && (allowlist.get(code) ?? []).includes(persona);
      }),
    }))
    .filter((g) => g.flagResponse.length > 0);
}
